import React, { useMemo } from 'react';
import cn from 'clsx';

import Typography from '../../library/typography/typography';
import DollarCircleSolidIcon from '../../shared/icons/circle-dollar-solid-icon';
import {
  MedalSolidIcon,
  PennantSolidIcon,
  TagsSolidIcon
} from '../../shared/icons/icons';
import Flag from '../flag/flag';
import TreadwellRankBanner from '../treadwell-rank-banner/treadwell-rank-banner';
import WebOnlyFlag from '../web-only-flag/web-only-flag';

import {
  getPrioritizedArticleStatusProduct,
  isWebOnlyProduct
} from '../../shared/utils/product/product';

import { BundleData, ProductData } from '../../shared/types/graphql-types';
import {
  ARTICLE_STATUSES,
  PROMOTION_GROUP
} from '../../shared/constants/products';

import styles from './product-flags.scss';

interface FlaggedProduct {
  hasPromo?: boolean;
  hasRebate?: boolean;
  isExclusive?: boolean;
  isFeatured?: boolean;
}

interface ProductFlagsProps {
  className?: string;
  hideFeaturedAndExclusive?: boolean;
  hideLabel?: boolean;
  hideTreadwellRank?: boolean;
  hideWebOnly?: boolean;
  prebuiltPackage?: BundleData;
  product?: ProductData;
}

function ProductFlags(props: ProductFlagsProps) {
  const {
    className = '',
    hideFeaturedAndExclusive = false,
    hideLabel = false,
    hideTreadwellRank = false,
    hideWebOnly = false,
    prebuiltPackage,
    product
  } = props;

  const { product1, product2 } = prebuiltPackage || {};
  const isDiscontinued = prebuiltPackage
    ? getPrioritizedArticleStatusProduct(product1, product2)?.articleStatus ===
      ARTICLE_STATUSES.DISCONTINUED
    : product?.articleStatus === ARTICLE_STATUSES.DISCONTINUED;

  const isBackordered = prebuiltPackage
    ? getPrioritizedArticleStatusProduct(product1, product2)?.articleStatus ===
      ARTICLE_STATUSES.BACKORDER
    : product?.articleStatus === ARTICLE_STATUSES.BACKORDER;

  const isTemporarilyUnavailable = prebuiltPackage
    ? getPrioritizedArticleStatusProduct(product1, product2)?.articleStatus ===
      ARTICLE_STATUSES.TEMPORARILY_UNAVAILABLE
    : product?.articleStatus === ARTICLE_STATUSES.TEMPORARILY_UNAVAILABLE;

  const { rank } = product?.recommendationData || {};
  const { origEquipmentData } = product || {};
  const isOriginalEquipment = Boolean(origEquipmentData);
  const isWebOnly = isWebOnlyProduct(product);

  const { hasPromo, hasRebate, isExclusive, isFeatured } = useMemo(
    () =>
      [product, product1, product2].reduce(
        (flaggedProduct: FlaggedProduct, currentProduct): FlaggedProduct => {
          const { isCoreProduct, isExclusive, potentialPromotions } =
            currentProduct || {};

          const hasPromo = potentialPromotions?.some(
            promotion =>
              promotion?.promotionGroup !==
              PROMOTION_GROUP.MAIL_IN_REBATE_PROMO_RULE_GROUP
          );

          const hasRebate = potentialPromotions?.some(
            promotion =>
              promotion?.promotionGroup ===
              PROMOTION_GROUP.MAIL_IN_REBATE_PROMO_RULE_GROUP
          );
          return {
            hasPromo: flaggedProduct.hasPromo || hasPromo,
            hasRebate: flaggedProduct.hasRebate || hasRebate,
            isExclusive: Boolean(flaggedProduct.isExclusive || isExclusive),
            isFeatured: Boolean(flaggedProduct.isFeatured || isCoreProduct)
          };
        },
        {
          hasPromo: false,
          hasRebate: false,
          isExclusive: false,
          isFeatured: false
        }
      ),
    [product, product1, product2]
  );

  const isFeaturedOrExclusive = isFeatured || isExclusive;

  const getFlags = () => {
    const flags: JSX.Element[] = [];

    if (!hideTreadwellRank && rank) {
      flags.push(
        <TreadwellRankBanner
          className={cn(
            styles['tw-rank-banner'],
            'product-flags__treadwell-rank-banner'
          )}
          hideLabel={hideLabel}
          key="treadwell"
          rank={rank}
        />
      );
    }

    if (!isDiscontinued && !isBackordered && !isTemporarilyUnavailable) {
      if (
        isFeaturedOrExclusive &&
        hideTreadwellRank &&
        !hideFeaturedAndExclusive
      ) {
        flags.push(
          <Flag
            className={`${styles['flag']} ${
              isExclusive ? styles['exclusive-flag'] : styles['featured-flag']
            }`}
            key="featuredExclusive"
            renderIcon={
              isExclusive ? (
                <MedalSolidIcon className={styles['flag-icon']} />
              ) : (
                <PennantSolidIcon className={styles['flag-icon']} />
              )
            }
          >
            {!hideLabel ? `${isExclusive ? 'EXCLUSIVE' : 'FEATURED'}` : ''}
          </Flag>
        );
      }

      if (hasPromo) {
        flags.push(
          <Flag
            className={`${styles['flag']} ${styles['savings-flag']}`}
            key="savings"
            renderIcon={<TagsSolidIcon className={styles['flag-icon']} />}
          >
            {!hideLabel ? 'SAVINGS' : ''}
          </Flag>
        );
      }

      if (
        hasRebate &&
        !hasPromo &&
        (flags.length < 2 || (flags.length === 2 && !isOriginalEquipment))
      ) {
        flags.push(
          <Flag
            className={`${styles['flag']} ${styles['rebate-flag']}`}
            key="rebate"
            renderIcon={
              <DollarCircleSolidIcon className={styles['flag-icon']} />
            }
          >
            {!hideLabel ? 'REBATE AVAILABLE' : ''}
          </Flag>
        );
      }
    }

    if (
      isOriginalEquipment &&
      (flags.length < 3 || (flags.length === 2 && hasRebate))
    ) {
      flags.push(
        <Flag
          className={`${styles['flag']} ${styles['oe-flag']}`}
          key="oe"
          renderIcon={
            <Typography
              className={`${styles['flag-icon']} ${styles['oe-flag-icon']}`}
              size="small"
              tag="span"
              weight="black"
            >
              O.E.
            </Typography>
          }
        >
          {!hideLabel ? ' ORIGINAL EQUIPMENT' : ''}
        </Flag>
      );
    }

    if (!hideWebOnly && isWebOnly) {
      flags.push(
        <WebOnlyFlag
          className={`${styles['flag']} ${styles['web-only-flag']}`}
          hideLabel={hideLabel}
          key="web-only"
        />
      );
    }

    return flags;
  };

  return (
    <span
      className={cn(styles['container'], `product-flags-one-dt ${className}`)}
    >
      {getFlags()}
    </span>
  );
}

export default ProductFlags;
